import { supabase } from './supabase'

/**
 * Feature flag primitives (W4 — 2026-09-11).
 *
 * Per-family gradual-rollout switches backed by the `feature_flags` Supabase
 * table (see 20260911140000_feature_flags.sql). Reads are RLS-scoped to the
 * caller's active families via `is_active_family_member`. Writes are service-
 * role only; toggling flags is an admin operation for now.
 *
 * Design intent: off-by-default is the safe posture. A missing row for a
 * (family, flag) pair means "not enabled" — never throw, never crash the UI.
 * New flags start invisible; enabling them is an explicit, auditable act.
 *
 * Prefer the React hook (`useFeatureFlag`) over calling `getFlag` directly
 * in components — the hook subscribes to future flag changes via Supabase
 * Realtime so gradual rollouts don't need a page refresh.
 */

/**
 * Read a single flag for a family. Returns `false` if the row doesn't exist,
 * if the query errors, or if `familyId` is missing.
 *
 * Safe to call from server-side edge functions (uses same supabase client
 * with anon/user JWT — RLS applies).
 */
export async function getFlag(familyId: string | null | undefined, key: string): Promise<boolean> {
  if (!familyId) return false
  const { data, error } = await supabase
    .from('feature_flags')
    .select('enabled')
    .eq('family_id', familyId)
    .eq('flag_key', key)
    .maybeSingle()
  if (error || !data) return false
  return data.enabled === true
}

/**
 * Read all flags for a family into a plain map. Useful for a single fetch on
 * app boot rather than one round-trip per flag.
 */
export async function getAllFlags(familyId: string | null | undefined): Promise<Record<string, boolean>> {
  const out: Record<string, boolean> = {}
  if (!familyId) return out
  const { data, error } = await supabase
    .from('feature_flags')
    .select('flag_key, enabled')
    .eq('family_id', familyId)
  if (error || !Array.isArray(data)) return out
  for (const row of data) {
    if (row?.flag_key && typeof row.enabled === 'boolean') {
      out[row.flag_key] = row.enabled
    }
  }
  return out
}
